/**
 * QL-117 — goal-page position helpers.
 *
 * A goal page renders inside its goal either before the goal's portfolio pages (the historical
 * position, `goalPosition = "before_portfolios"`) or after them (`"after_portfolios"`). The
 * runtime row draws them as `[goal pages before] [portfolio dimension] [goal pages after]`, so
 * the shared per-goal view splits one goal-page list into the two groups here. Pure helpers,
 * no React — the same split serves the advisor wizard and the public flow.
 */

import type { GoalPosition } from "@repo/shared/types";

type PositionedPage = { goalPosition?: GoalPosition | string | null; order: number };

/** Unknown or missing values (an older cached payload) read as the DB default. */
export function normaliseGoalPosition(value: unknown): GoalPosition {
  return value === "after_portfolios" ? "after_portfolios" : "before_portfolios";
}

/**
 * Split a goal's pages around the portfolio dimension. `order` is only comparable inside one
 * section, so each group is sorted on its own rather than trusting the combined list's order.
 */
export function splitGoalPagesByPosition<P extends PositionedPage>(
  goalPages: readonly P[],
): { before: P[]; after: P[] } {
  const byOrder = (a: P, b: P) => (a.order ?? 0) - (b.order ?? 0);
  const before: P[] = [];
  const after: P[] = [];
  for (const page of goalPages) {
    (normaliseGoalPosition(page.goalPosition) === "after_portfolios" ? after : before).push(page);
  }
  return { before: before.sort(byOrder), after: after.sort(byOrder) };
}
